import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Upload, Bot, ChevronRight, DollarSign, Filter, X, Bell, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Orthopedic', 'Spine', 'Ophthalmology', 'Bariatric', 'General Surgery', 'Gastroenterology', 'Gynecology', 'ENT', 'Plastic Surgery', 'Cardiology', 'Urology', 'Podiatry', 'Hand Surgery', 'ENT/Endocrine'];

export default function ProceduresPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeCategory, setActiveCategory] = useState('All');
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTab, setActiveTab] = useState('browse'); // browse, ai, upload

  // Price alert state
  const [alertEmail, setAlertEmail] = useState('');
  const [alertMaxPrice, setAlertMaxPrice] = useState('');
  const [alertState, setAlertState] = useState('');
  const [alertStatus, setAlertStatus] = useState(null); // null | 'success' | 'error'
  const [alertMessage, setAlertMessage] = useState('');
  const [alertLoading, setAlertLoading] = useState(false);

  const fetchProcedures = async (query = '', category = '') => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.search = query;
      if (category && category !== 'All') params.category = category;
      const res = await axios.get(`${API}/procedures`, { params });
      setProcedures(res.data);
    } catch (e) {
      console.error('Failed to fetch procedures', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcedures(searchQuery, activeCategory);
  }, [searchQuery, activeCategory]);

  // 2a: Detect whether the query contains a location signal.
  // If so, redirect to the AI-powered search which handles geo correctly.
  const hasLocationKeyword = (query) => {
    const q = query.toLowerCase();
    if (/\b(in|near|close to|around)\b/.test(q)) return true;
    const stateNames = [
      'alabama','alaska','arizona','arkansas','california','colorado','connecticut',
      'delaware','florida','georgia','hawaii','idaho','illinois','indiana','iowa',
      'kansas','kentucky','louisiana','maine','maryland','massachusetts','michigan',
      'minnesota','mississippi','missouri','montana','nebraska','nevada',
      'new hampshire','new jersey','new mexico','new york','north carolina',
      'north dakota','ohio','oklahoma','oregon','pennsylvania','rhode island',
      'south carolina','south dakota','tennessee','texas','utah','vermont',
      'virginia','washington','west virginia','wisconsin','wyoming',
    ];
    if (stateNames.some(s => q.includes(s))) return true;
    // 2-letter state abbreviation surrounded by word boundaries
    const abbrevs = [
      'al','ak','az','ar','ca','co','ct','de','fl','ga','hi','id','il','in','ia',
      'ks','ky','la','me','md','ma','mi','mn','ms','mo','mt','ne','nv','nh','nj',
      'nm','ny','nc','nd','oh','ok','or','pa','ri','sc','sd','tn','tx','ut','vt',
      'va','wa','wv','wi','wy',
    ];
    return abbrevs.some(a => new RegExp(`\\b${a}\\b`).test(q));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // 2b/2c: Redirect location queries to AI search, otherwise search locally
    if (searchQuery.trim() && hasLocationKeyword(searchQuery)) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      fetchProcedures(searchQuery, activeCategory);
    }
  };

  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    if (!alertEmail) { setAlertStatus('error'); setAlertMessage('Email is required.'); return; }
    setAlertLoading(true);
    setAlertStatus(null);
    try {
      const payload = {
        email: alertEmail,
        procedure_keyword: searchQuery || undefined,
        state: alertState || undefined,
        max_price_threshold: alertMaxPrice ? parseFloat(alertMaxPrice) : undefined,
      };
      if (!payload.procedure_keyword) {
        setAlertStatus('error');
        setAlertMessage('Please enter a procedure search term first, then set a price alert.');
        setAlertLoading(false);
        return;
      }
      await axios.post(`${API}/alerts/subscribe`, payload);
      setAlertStatus('success');
      setAlertMessage(`Price alert set. We'll email you at ${alertEmail} when prices change.`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not set alert. Please check your email and try again.';
      setAlertStatus('error');
      setAlertMessage(msg);
    } finally {
      setAlertLoading(false);
    }
  };

  const handleAiSearch = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    setAiResults(null);
    try {
      const res = await axios.post(`${API}/ai/identify-procedure`, { message: aiQuery });
      setAiResults(res.data);
    } catch (e) {
      setAiResults({ procedures: [], explanation: 'Could not identify procedure. Please try again.' });
    } finally {
      setAiLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploadLoading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      const res = await axios.post(`${API}/ai/analyze-document`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
    } catch (e) {
      setUploadResult({ analysis: 'Document analysis failed. Please try again with a clear image or PDF.', error: true });
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Procedure Search</p>
          <h1 className="text-4xl font-medium text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Find Your Procedure</h1>
          <p className="text-stone-600 max-w-2xl">Search by procedure name, CPT code, or use our AI tools to identify your procedure from a description or uploaded medical document.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Tab Switcher */}
        <div className="flex gap-0 mb-8 border border-stone-200 bg-white w-fit">
          {[
            { id: 'browse', label: 'Browse Procedures', icon: Search },
            { id: 'ai', label: 'AI Search', icon: Bot },
            { id: 'upload', label: 'Upload Document', icon: Upload },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-[#0D3D4A] text-white' : 'text-stone-600 hover:bg-[#F0EEE9]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <>
          <div>
            <form onSubmit={handleSearch} className="flex gap-0 mb-1 max-w-2xl">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8B4B8]" size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search procedures, or 'knee replacement in Oklahoma'..."
                  className="w-full pl-11 pr-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                  data-testid="procedure-search-input"
                />
              </div>
              <button type="submit" className="bg-[#0D3D4A] text-white px-6 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors" data-testid="procedure-search-btn">
                Search
              </button>
            </form>
            <p className="text-xs text-stone-400 mt-1 mb-6">
              Tip: Include a location (e.g. "in Texas") to find providers near you with pricing.
            </p>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-8">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 text-xs font-medium transition-colors border ${
                    activeCategory === cat ? 'bg-[#0D3D4A] text-white border-[#0D3D4A]' : 'bg-white text-stone-600 border-stone-200 hover:border-[#A8B4B8]'
                  }`}
                  data-testid={`category-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Procedures Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({length: 6}).map((_, i) => (
                  <div key={i} className="bg-white border border-stone-200 p-6 animate-pulse">
                    <div className="h-4 bg-stone-100 rounded w-3/4 mb-3" />
                    <div className="h-3 bg-stone-100 rounded w-1/4 mb-4" />
                    <div className="h-3 bg-stone-100 rounded w-full mb-2" />
                    <div className="h-3 bg-stone-100 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : procedures.length === 0 ? (
              <div className="text-center py-16">
                <Search size={40} className="text-[#A8B4B8] mx-auto mb-4" />
                <p className="text-stone-500">No procedures found. Try a different search or use AI Search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-stagger">
                {procedures.map(proc => (
                  <button
                    key={proc.id}
                    onClick={() => navigate(`/providers?cpt=${proc.cpt}&procedure=${encodeURIComponent(proc.name)}&proc_id=${proc.id}`)}
                    className="bg-white border border-stone-200 p-6 text-left card-hover group"
                    data-testid={`procedure-card-${proc.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-semibold text-[#0D3D4A] bg-[#F0EEE9] px-2 py-1">{proc.category}</span>
                      <span className="text-xs text-[#A8B4B8] font-mono">CPT {proc.cpt}</span>
                    </div>
                    <h3 className="font-semibold text-stone-900 mb-2 text-sm leading-snug group-hover:text-[#0D3D4A] transition-colors" style={{fontFamily: 'Outfit, sans-serif'}}>{proc.name}</h3>
                    <p className="text-xs text-stone-500 mb-4 leading-relaxed line-clamp-2">{proc.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                      <div>
                        <p className="text-xs text-[#A8B4B8]">Cash price from</p>
                        <p className="font-bold text-[#0D3D4A]">${proc.avg_cash_price?.toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#A8B4B8]">vs. Ins. OOP est.</p>
                        <p className="font-medium text-[#A8B4B8] line-through text-sm">${proc.avg_insurance_oop?.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end mt-3 text-[#0D3D4A] text-xs font-medium">
                      Find Providers <ChevronRight size={14} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Price Alert Subscription Card */}
          <div className="mt-10 bg-[#F0EEE9] border border-[#0D3D4A]/20 p-8" data-testid="price-alert-card">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-[#0D3D4A] flex items-center justify-center flex-shrink-0">
                <Bell size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Get notified when prices drop</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Enter your email to receive a price alert when cash prices change for{' '}
                  <strong>{searchQuery || 'this procedure'}</strong>
                  {alertState ? ` in ${alertState}` : ' in your selected state'}.
                </p>
              </div>
            </div>

            {alertStatus === 'success' ? (
              <div className="flex items-center gap-3 p-4 bg-white border border-[#0D3D4A]/30" data-testid="alert-success-msg">
                <CheckCircle size={18} className="text-[#0D3D4A] flex-shrink-0" />
                <p className="text-sm text-[#0D3D4A] font-medium">{alertMessage}</p>
              </div>
            ) : (
              <form onSubmit={handleAlertSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={alertEmail}
                  onChange={e => setAlertEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                  data-testid="alert-email-input"
                />
                <input
                  type="number"
                  value={alertMaxPrice}
                  onChange={e => setAlertMaxPrice(e.target.value)}
                  placeholder="Max price (optional)"
                  min="0"
                  className="w-full sm:w-44 px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                  data-testid="alert-max-price-input"
                />
                <input
                  type="text"
                  value={alertState}
                  onChange={e => setAlertState(e.target.value.toUpperCase().slice(0,2))}
                  placeholder="State (e.g. FL)"
                  maxLength={2}
                  className="w-full sm:w-28 px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] uppercase"
                  data-testid="alert-state-input"
                />
                <button
                  type="submit"
                  disabled={alertLoading}
                  className="bg-[#0D3D4A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
                  data-testid="alert-submit-btn"
                >
                  <Bell size={14} />
                  {alertLoading ? 'Setting...' : 'Set Price Alert'}
                </button>
              </form>
            )}

            {alertStatus === 'error' && (
              <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200" data-testid="alert-error-msg">
                <AlertCircle size={15} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{alertMessage}</p>
              </div>
            )}
          </div>
        </>
        )}

        {/* AI Search Tab */}
        {activeTab === 'ai' && (
          <div className="max-w-2xl">
            <div className="bg-white border border-stone-200 p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#0D3D4A] flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>AI Procedure Identifier</h3>
                  <p className="text-xs text-stone-500">Describe your condition or what your doctor recommended</p>
                </div>
              </div>
              <form onSubmit={handleAiSearch}>
                <textarea
                  value={aiQuery}
                  onChange={e => setAiQuery(e.target.value)}
                  placeholder="e.g. 'My doctor says I need surgery for a torn meniscus in my right knee' or 'I have been diagnosed with severe GERD and my doctor recommends surgery'"
                  rows={4}
                  className="w-full px-4 py-3 border border-[#A8B4B8] bg-[#F0EEE9] text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] resize-none mb-4"
                  data-testid="ai-search-input"
                />
                <button
                  type="submit"
                  disabled={aiLoading || !aiQuery.trim()}
                  className="bg-[#0D3D4A] text-white px-8 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="ai-search-submit"
                >
                  {aiLoading ? 'Analyzing...' : 'Identify Procedure'} <Bot size={15} />
                </button>
              </form>
            </div>

            {aiResults && (
              <div className="bg-white border border-stone-200 p-8 animate-fade-in" data-testid="ai-results">
                <h3 className="font-semibold text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>AI Analysis Result</h3>
                {aiResults.procedures && aiResults.procedures.length > 0 ? (
                  <>
                    <p className="text-sm text-stone-600 mb-6">{aiResults.explanation}</p>
                    <div className="space-y-3">
                      {aiResults.procedures.map((proc, i) => (
                        <div key={i} className="border border-stone-200 p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-stone-900 text-sm">{proc.name}</h4>
                            <span className="text-xs font-mono text-[#0D3D4A] bg-[#F0EEE9] px-2 py-1">CPT {proc.cpt}</span>
                          </div>
                          <p className="text-xs text-stone-600 mb-3">{proc.description}</p>
                          <button
                            onClick={() => navigate(`/providers?cpt=${proc.cpt}&procedure=${encodeURIComponent(proc.name)}`)}
                            className="text-xs text-[#0D3D4A] font-medium hover:text-[#0D3D4A] flex items-center gap-1"
                          >
                            Find providers for this procedure <ChevronRight size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-stone-600 whitespace-pre-wrap">{aiResults.explanation}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload Document Tab */}
        {activeTab === 'upload' && (
          <div className="max-w-2xl">
            <div className="bg-white border border-stone-200 p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0D3D4A] flex items-center justify-center">
                  <Upload size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>Upload Medical Document</h3>
                  <p className="text-xs text-stone-500">Referral letters, clinical notes, consultation summaries — supported formats: JPG, PNG, WEBP, PDF</p>
                </div>
              </div>

              <form onSubmit={handleFileUpload}>
                <div
                  className="border-2 border-dashed border-[#A8B4B8] p-10 text-center mb-6 hover:border-[#0D3D4A] transition-colors cursor-pointer"
                  onClick={() => document.getElementById('file-upload').click()}
                  data-testid="file-drop-zone"
                >
                  <Upload size={32} className="text-[#A8B4B8] mx-auto mb-3" />
                  <p className="text-sm text-stone-600 mb-1">Click to upload or drag and drop</p>
                  <p className="text-xs text-[#A8B4B8]">JPG, PNG, WEBP, PDF supported</p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={e => setUploadFile(e.target.files[0])}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                </div>

                {uploadFile && (
                  <div className="flex items-center justify-between bg-[#F0EEE9] border border-[#0D3D4A]/20 p-3 mb-4">
                    <span className="text-sm text-[#0D3D4A] font-medium">{uploadFile.name}</span>
                    <button type="button" onClick={() => setUploadFile(null)} className="text-[#0D3D4A] hover:text-[#0D3D4A]">
                      <X size={16} />
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={uploadLoading || !uploadFile}
                  className="bg-[#0D3D4A] text-white px-8 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="upload-analyze-btn"
                >
                  {uploadLoading ? 'Analyzing document...' : 'Analyze Document'} <Bot size={15} />
                </button>
              </form>
            </div>

            {uploadResult && (
              <div className={`bg-white border p-8 animate-fade-in ${uploadResult.error ? 'border-red-200' : 'border-stone-200'}`} data-testid="upload-result">
                <h3 className="font-semibold text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Document Analysis</h3>
                {uploadResult.filename && (
                  <p className="text-xs text-[#A8B4B8] mb-4">Analyzed: {uploadResult.filename}</p>
                )}
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{uploadResult.analysis}</p>
                </div>
                {!uploadResult.error && (
                  <div className="mt-6 pt-6 border-t border-stone-100">
                    <button onClick={() => setActiveTab('browse')} className="text-sm text-[#0D3D4A] font-medium hover:text-[#0D3D4A]">
                      Search for this procedure in our database
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
